/**
 * Timeline — la historia de un documento, en orden.
 *
 * Es el recorrido de un DTE (generado → firmado → enviado → aceptado), el
 * historial de una solicitud de vacaciones o el de una factura en cobranza.
 *
 * ⚠️ Es una **`<ol>`**, no una pila de `<div>`. El orden es parte del dato: un
 * lector de pantalla anuncia «lista de 5 elementos» y numera cada paso, que es
 * exactamente lo que una línea de tiempo significa.
 *
 * ⚠️ La columna de puntos y líneas va `aria-hidden`: es el dibujo de algo que
 * el orden de la lista ya expresa. Leerla en voz alta sería ruido.
 */
import type { HTMLAttributes, ReactNode } from 'react'

export interface TimelineItem {
  label: ReactNode
  /** Ya formateado en la hora local del país del tenant, no en UTC. */
  time?: ReactNode
  /** El detalle: el motivo de un rechazo, la glosa del SII. */
  note?: ReactNode
  /** Un color CSS completo para el punto. */
  tone?: string
}

export interface TimelineProps extends Omit<HTMLAttributes<HTMLOListElement>, 'className'> {
  items?: TimelineItem[]
  className?: string
}

export function Timeline({ items = [], className = '', ...rest }: TimelineProps) {
  return (
    <ol
      className={className}
      style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}
      {...rest}
    >
      {items.map((it, i) => (
        <li key={i} style={{ display: 'flex', gap: 12 }}>
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 'none' }}
            aria-hidden="true"
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: it.tone || 'rgb(var(--color-content-subtle))',
                boxShadow: `0 0 0 2px ${it.tone || 'transparent'}`,
              }}
            />
            {i < items.length - 1 && (
              <span
                style={{
                  flex: 1,
                  width: 1.5,
                  background: 'rgb(var(--color-divider))',
                  minHeight: 22,
                }}
              />
            )}
          </div>
          <div style={{ paddingBottom: 14, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600 }}>{it.label}</div>
            {it.time && (
              <div
                className="cl-mono"
                style={{ fontSize: 11, color: 'rgb(var(--color-content-subtle))', marginTop: 2 }}
              >
                {it.time}
              </div>
            )}
            {it.note && (
              <div
                className="cl-surface-2"
                style={{
                  fontSize: 11.5,
                  color: 'rgb(var(--color-content-muted))',
                  marginTop: 5,
                  padding: '7px 9px',
                  borderRadius: 9,
                }}
              >
                {it.note}
              </div>
            )}
          </div>
        </li>
      ))}
    </ol>
  )
}
